import React, { useState, useEffect } from "react";
import { Card } from "antd";
import { Briefcase, Calendar, Clock, Eye, MapPin, ClipboardList } from "lucide-react";
import AssessmentProgress from "./AssessmentProgress";
import HeaderModal from "components/modal/HeaderModal";
import ViewJobPost from "../view";

const MyAssessmentsPanel = ({ application }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedApp, setSelectedApp] = useState(null); 

  if (!application) return null; 

  return (
      <Card>
        <div className="p-5 space-y-8">

          <div className="flex flex-col items-center justify-center text-center space-y-8">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto">
              <ClipboardList className="w-8 h-8 text-gray-400"/>
            </div>
            <h2 className="text-gray-800 text-lg font-medium">
              No Applications Yet
            </h2>
            <p className="text-base">You haven't applied to any positions yet. Browse our open roles and apply to get started!</p>
            <button className="btn-gradient-emerald px-3 py-1.5 flex rounded-lg w-full">
              View Available Jobs
            </button>
          </div>
        </div>

        {application.nextStep && (
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-emerald-50 border border-purple-200 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-purple-900 mb-1">
                  <strong>Next Step:</strong>
                </p>
                <p className="text-sm text-purple-800">{application.nextStep.title}</p>
                <p className="text-xs text-purple-700 mt-1">{application.nextStep.scheduledFor}</p>
              </div>
            </div>
          </div>
        )}

        <h4 className="text-sm text-gray-800 font-medium">Assessment Progress</h4>
        {application.progress && <AssessmentProgress progress={application.progress} />}

        {/* Assessment Scores */}
        {application.assessmentScores?.length > 0 && (
          <div className="pt-4 border-t border-gray-200">
            <h4 className="text-sm text-gray-800 mb-3 font-medium">Assessment Scores</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {application.assessmentScores.map((score, index) => (
                <div key={index}>
                  <p className="text-xs text-gray-500 mb-1">{score.label} Score</p>
                  <p className="text-sm text-gray-700">{score.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Skills */}
        {application.skills?.length > 0 && (
          <div className="pt-4 border-t border-gray-200">
            <h4 className="text-sm text-gray-800 mb-3 font-medium">Skills Assessment</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {application.skills.map((skill, index) => (
                <div key={index}>
                  <p className="text-xs text-gray-500 mb-1">{skill.name}</p>
                  <p className="text-sm text-gray-700">Level: {skill.level}/5</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Detailed Assessments */}
        {application.detailedAssessments?.length > 0 && (
          <div className="pt-4 border-t border-gray-200">
            <h4 className="text-sm text-gray-800 mb-3 font-medium">Assessment Breakdown</h4>
            {application.detailedAssessments.map((assessment, idx) => (
              <div key={idx} className="mb-6">
                <p className="text-sm text-gray-700 mb-1">{assessment.title}</p>
                <p className="text-xs text-gray-500 mb-2">
                  Score: {assessment.totalScore}/{assessment.maxScore}
                </p>
                <p className="text-xs text-gray-500 mb-3">Completed on {assessment.completedOn}</p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {assessment.breakdown.map((item, i) => (
                    <div key={i}>
                      <p className="text-xs text-gray-500 mb-1">{item.name}</p>
                      <p className="text-sm text-gray-700">
                        Score: {item.score}/{item.max}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Overall Feedback */}
        {application.overallFeedback?.trim() && (
          <div className="pt-4 border-t border-gray-200">
            <h4 className="text-sm text-gray-800 mb-3 font-medium">Overall Feedback</h4>
            <p className="text-sm text-gray-700">{application.overallFeedback}</p>
          </div>
        )}

        <div className="pt-4 border-t border-gray-200">
          <button
            className="inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium btn-gradient-emerald hover:from-purple-700 hover:to-emerald-700 h-9 px-4 py-2"
            onClick={() => {
              setSelectedApp(application); 
              setIsOpen(true); 
            }}
          >
            <Eye className="w-4 h-4 mr-2" /> View Details
          </button>

            {selectedApp && (
                <HeaderModal
                  title={`${selectedApp.title}`}
                  titleClass="font-medium text-xl"
                  description={<div className="flex flex-wrap items-center gap-4 text-sm text-gray-700">
                                  <div className="flex items-center gap-1">
                                    <Briefcase className="w-4 h-4" />
                                    <span>{selectedApp.department}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <MapPin className="w-4 h-4" />
                                    <span>{selectedApp.location}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    <span>Applied {selectedApp.appliedOn}</span>
                                  </div>
                                </div>}
                  descriptionClass="py-2"
                  bgcolor="border-b border-gray-200 bg-gradient-to-r from-purple-50 to-emerald-50"
                  open={isOpen}
                  onClose={() => setIsOpen(false)}
                  colorTheme="w-14 h-14 bg-white/20 backdrop-blur-lg rounded-2xl flex items-center justify-center border border-white/30"
                  width={900}
                  footer={<div className="w-full flex items-end justify-end p-4">
                    <button onClick={() => setIsOpen(false)} className="btn-gradient-emerald px-4 py-1 rounded-md">Close</button>
                  </div>}
                >
                  <ViewJobPost application={selectedApp} />
                </HeaderModal>
              )}
        </div>
    </Card>
  );
};

export default MyAssessmentsPanel;
